/**
 * userShared.js
 *
 * The presentational pieces every staff page uses, so the five sections read
 * as one product instead of five.
 *
 * Everything lives at module scope on purpose: a component declared inside
 * another component is a new type on every render, which unmounts and
 * remounts its subtree - and a focused input inside it loses focus after a
 * single keystroke.
 */

import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";

/* ─── Status vocabulary ────────────────────────────────────────────────── */

export const STATUS_TEXT = {
  0: "Pending",
  1: "In progress",
  2: "Approved",
  3: "Rejected",
  4: "Returned",
};

/**
 * One tone per status, used for the chip and the card's left edge. The old
 * pages painted the WHOLE card in a pale status colour and forced black text
 * on it, which broke in dark mode; the colour now lives on an edge and a
 * chip so the surface stays readable in both themes.
 */
export const statusTone = (colors, status) =>
  ({
    0: colors.orange[500],
    1: colors.newTeal[500],
    2: colors.green[500],
    3: colors.red[500],
    4: colors.teal[500],
  }[Number(status)] || colors.gray[500]);

export const StatusChip = ({ colors, status, size = "small" }) => {
  const tone = statusTone(colors, status);
  return (
    <Chip
      size={size}
      label={STATUS_TEXT[Number(status)] || "Unknown"}
      sx={{
        fontWeight: 700,
        color: tone,
        bgcolor: alpha(tone, 0.16),
        border: `1px solid ${alpha(tone, 0.35)}`,
      }}
    />
  );
};

/* ─── Field and button styles ─────────────────────────────────────────── */

export const fieldSx = (ui) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: ui.surfaceAlt,
    color: ui.textPrimary,
    "& fieldset": { borderColor: ui.border },
    "&:hover fieldset": { borderColor: ui.accent },
    "&.Mui-focused fieldset": { borderColor: ui.accent },
  },
  "& .MuiInputLabel-root": { color: ui.textSecondary },
  "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
});

export const primaryBtn = (ui) => ({
  background: ui.gradient,
  color: "#fff",
  borderRadius: 2,
  px: 2.5,
  py: 0.9,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { boxShadow: "none", filter: "brightness(1.08)" },
  "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
});

export const outlineBtn = (ui, tone) => ({
  color: tone || ui.accent,
  border: `1px solid ${alpha(tone || ui.accent, 0.5)}`,
  borderRadius: 2,
  px: 2,
  py: 0.8,
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    bgcolor: alpha(tone || ui.accent, 0.08),
    border: `1px solid ${tone || ui.accent}`,
  },
});

export const dialogPaperSx = (ui) => ({
  bgcolor: ui.surface,
  borderRadius: "16px",
  border: `1px solid ${ui.border}`,
  backgroundImage: "none",
});

/* ─── Panels ──────────────────────────────────────────────────────────── */

export const SectionCard = ({ ui, title, hint, action, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: 3,
      boxShadow: ui.shadow,
      p: { xs: 2, md: 2.5 },
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        mb={hint ? 0.5 : 2}
      >
        <Typography variant="h5" fontWeight={700} color={ui.textPrimary}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {hint && (
      <Typography variant="body2" color={ui.textSecondary} mb={2}>
        {hint}
      </Typography>
    )}
    {children}
  </Paper>
);

/** A compact metric. One line of label, one number, one line of context. */
export const StatCard = ({ ui, icon, label, value, sub, tone }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.75,
      py: 1.5,
      height: "100%",
      borderRadius: 2,
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderLeft: `3px solid ${tone || ui.accent}`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    {icon && (
      <Box
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "grid",
          placeItems: "center",
          color: tone || ui.accent,
          bgcolor: alpha(tone || ui.accent, 0.14),
        }}
      >
        {icon}
      </Box>
    )}

    <Box sx={{ minWidth: 0 }}>
      <Typography
        noWrap
        sx={{
          fontSize: 10.5,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          fontWeight: 700,
          color: ui.textSecondary,
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 22, fontWeight: 800, color: ui.textPrimary, lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography noWrap sx={{ fontSize: 11, color: ui.textSecondary }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Paper>
);

/**
 * A record card: status on the left edge, a chip for the state, and rows of
 * label/value beneath. Replaces the pale full-colour cards.
 */
export const RecordCard = ({ ui, colors, status, title, chip, children, action }) => {
  const tone = statusTone(colors, status);
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        p: 2,
        borderRadius: 2.5,
        bgcolor: ui.surface,
        border: `1px solid ${ui.border}`,
        borderLeft: `4px solid ${tone}`,
        boxShadow: ui.shadow,
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1.5}
        mb={1}
      >
        <Typography variant="h5" fontWeight={700} color={ui.textPrimary}>
          {title}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {chip ?? <StatusChip colors={colors} status={status} />}
          {action}
        </Stack>
      </Box>
      {children}
    </Paper>
  );
};

export const DetailRow = ({ ui, label, value, color }) => (
  <Box
    display="flex"
    flexWrap="wrap"
    gap={1}
    py={0.85}
    borderBottom={`1px solid ${alpha(ui.border, 0.6)}`}
  >
    <Typography
      variant="body2"
      sx={{ minWidth: 150, fontWeight: 600, color: ui.textSecondary }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ flex: 1, color: color || ui.textPrimary, whiteSpace: "pre-wrap" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

/* ─── States ──────────────────────────────────────────────────────────── */

export const Loading = ({ ui, label = "Loading…" }) => (
  <Box sx={{ display: "grid", placeItems: "center", py: 6, gap: 1.5 }}>
    <CircularProgress sx={{ color: ui.accent }} />
    <Typography variant="body2" color={ui.textSecondary}>
      {label}
    </Typography>
  </Box>
);

export const EmptyState = ({ ui, icon, title, hint }) => (
  <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
    {icon && (
      <Box sx={{ color: alpha(ui.textSecondary, 0.6), mb: 1, "& svg": { fontSize: 44 } }}>
        {icon}
      </Box>
    )}
    <Typography variant="h6" fontWeight={700} color={ui.textPrimary}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="body2" color={ui.textSecondary} mt={0.5}>
        {hint}
      </Typography>
    )}
  </Box>
);
